/**
 * The banner under the Fit Score header while an interview is under way.
 * Renders nothing for `not_started` or `completed` -- this component only
 * exists for the two `in_progress` variants, so the caller simply doesn't
 * mount it outside those states rather than this component deciding that
 * for itself from a status enum it doesn't need to know about.
 *
 * "live" vs "stalled" is the same underlying data (how many dimensions have
 * an interview-sourced value); the wording changes to stay honest about
 * whether anything is currently happening, per the design spec's 5-minute
 * since-last-update threshold (computed by the caller, not this component).
 */

import { memo } from 'react';
import styles from './index.module.css';
import type { InterviewStatusVariant } from '../interview-status-badge';

export interface InterviewProgressBannerProps {
  variant: InterviewStatusVariant;
  /** Dimensions that now carry an interview-sourced value. */
  updatedCount: number;
  /** Total scored dimensions, e.g. 8. */
  totalCount: number;
}

export const InterviewProgressBanner = memo(
  ({ variant, updatedCount, totalCount }: InterviewProgressBannerProps) => (
    <p className={styles.banner}>
      {variant === 'live' ? (
        <>
          Interview in progress — Rio has updated {updatedCount} of {totalCount}{' '}
          dimensions so far.
        </>
      ) : (
        <>
          Interview started but not completed — showing partial results from{' '}
          {updatedCount} of {totalCount} dimensions.
        </>
      )}
    </p>
  ),
);

InterviewProgressBanner.displayName = 'InterviewProgressBanner';

export default InterviewProgressBanner;
